import type { StudioJobType } from '@/features/ai/studioTypes'
import type { useEditScope } from './useEditScope'
import type { SidebarMode } from './types'
import { messagesNow } from '@/i18n'
import { messages } from './sidebarMessages'

interface Args {
  mode: SidebarMode
  /** Область правки — тот же хук, которым кормится старт. `count` — есть ли
   *  что отправлять, `hasLessonBlocks` — различает «выделения нет» и
   *  «выделять нечего» (M-5), `isLoadingBlocks` — блоки урока ещё не
   *  ответили: `hasLessonBlocks` в этот момент ложно ЛЮБЫМ уроком, не только
   *  пустым, и утверждать «блоков нет» нельзя (регрессия финальной волны). */
  editScope: Pick<ReturnType<typeof useEditScope>, 'count' | 'hasLessonBlocks' | 'isLoadingBlocks'>
  isRunning: boolean
  /** Курс занят ЧУЖИМ прогоном (`busy.isBusy`) — ввод погашен не нами. */
  isBusy: boolean
  /** Курс на проверке — ввод погашен решением модерации, а не прогоном. */
  isFrozen: boolean
  /** Тип прогона, за которым СЕЙЧАС следит панель — различает подпись во
   *  время бега (правка блока говорит «Обработка...», как в рефе). */
  jobType: StudioJobType
}

/**
 * Плейсхолдер поля ввода. Раньше здесь была отдельная ветка `isThinking`
 * («Обработка...») для мок-ответа свободного чата — с мок-движком ушла и она;
 * с под-проектом E «Обработка...» вернулась, но теперь это подпись РЕАЛЬНОГО
 * прогона правки блока, а не заглушка (реф: `EditorAISidebar.tsx:1253`).
 */
export const resolvePlaceholder = ({
  mode,
  editScope,
  isRunning,
  isBusy,
  isFrozen,
  jobType,
}: Args): string => {
  const m = messagesNow(messages)
  if (isRunning) return jobType === 'course_block_edit' ? m.phProcessing : m.phGenerating
  // Ввод погашен чужим прогоном — приглашение «Опишите тему курса...» обещало
  // отправку, которой не будет, пока курс занят (B5).
  if (isBusy) return m.phBusy
  // Проверка курса гасит старт так же, как чужой прогон (`composerGates`), и
  // приглашение печатать обещало бы отправку, которой не будет до решения.
  if (isFrozen) return m.underReview
  if (mode === 'generate') return m.phGenerate
  if (mode === 'edit-block') {
    if (editScope.count > 0) return m.phEdit
    // Блоки урока ещё не ответили — `hasLessonBlocks` ложно ЛЮБЫМ уроком в
    // этот момент, не только пустым; «В уроке пока нет блоков» здесь было бы
    // конкретной неправдой (тот же дефект уже чинили в `EditBlockSettings`).
    if (editScope.isLoadingBlocks) return m.phLoadingBlocks
    // M-5: «выберите блок справа» в пустом уроке — указание, которое нельзя
    // выполнить; там же, где о том же говорит экран настроек, тот же текст.
    return editScope.hasLessonBlocks ? m.phSelectBlock : m.phNoBlocks
  }
  // Режим `idle` — поле заблокировано (свободного чата у панели нет), поэтому
  // плейсхолдер не зовёт печатать, а объясняет, что делать вместо этого.
  return m.phIdle
}
